/**
 * Single source of truth for static-page metadata.
 *
 * These titles and descriptions used to be typed inline in each page's <Seo>
 * call. That was fine while the meta tags only ever existed at runtime, but
 * scripts/prerender.js now writes them into the built HTML too — and two copies
 * of a page title in two places drift. When the prerendered <title> disagrees
 * with the one React swaps in on hydration, a crawler sees one page describing
 * itself two ways, which is the sort of inconsistency that costs trust.
 *
 * Both the pages and the build script read this file, so there is one copy.
 *
 * `changefreq` and `priority` are here rather than in the sitemap script for
 * the same reason: a route should be described once.
 */

export const STATIC_ROUTES = [
  {
    path: "/",
    title: "NT Sticks — Premium Dhoop Sticks & Incense, Direct from Our Indore Factory",
    description:
      "Charcoal-free, low-smoke dhoop sticks, incense and havan cups made at our own Indore factory. 23 fragrances, retail packs and wholesale cases.",
    changefreq: "weekly",
    priority: "1.0",
  },
  {
    path: "/shop",
    title: "Shop All Fragrances — Dhoop Sticks, Jars & Havan Cups | NT Sticks",
    description:
      "Browse every NT Sticks fragrance: sandal, mogra, rose, loban, guggal and more. Jars, boxes and zipper packs from ₹55, made fresh in Indore.",
    changefreq: "weekly",
    priority: "0.9",
  },
  {
    path: "/wholesale",
    title: "Wholesale & Bulk Orders | NT Sticks",
    description:
      "Request a bulk quote directly from NT Sticks factory in Indore. Get premium incense sticks, dhoop sticks and sambrani at competitive wholesale rates.",
    changefreq: "monthly",
    priority: "0.8",
  },
  {
    path: "/contact",
    title: "Contact NT Sticks — Incense Manufacturer in Indore",
    description:
      "Contact NT Sticks for retail orders, wholesale inquiries, and premium incense products from Indore.",
    changefreq: "monthly",
    priority: "0.7",
  },
  {
    path: "/blogs",
    // Deliberately not the Ganesh-specific `blog_title` from Data.js. That
    // string describes one seasonal post, not the index it was titling, so
    // the page advertised itself as being about Ganesh idols all year.
    title: "Incense & Puja Guides | NT Sticks",
    description:
      "Guides to dhoop, incense and sambrani — how they differ, how to use them, and how to choose a fragrance for daily puja.",
    changefreq: "monthly",
    priority: "0.6",
  },
  {
    path: "/terms",
    title: "Terms & Conditions | NT Sticks",
    description: "The terms that apply when you order from NT Sticks.",
    changefreq: "yearly",
    priority: "0.3",
  },
  {
    path: "/shipping",
    title: "Shipping & Delivery | NT Sticks",
    description:
      "How and when NT Sticks orders are dispatched from Indore, and what delivery costs.",
    changefreq: "yearly",
    priority: "0.3",
  },
  {
    path: "/returnrefund",
    title: "Returns & Refunds | NT Sticks",
    description:
      "NT Sticks return window, refund process and what to do if an order arrives damaged.",
    changefreq: "yearly",
    priority: "0.3",
  },
  {
    path: "/privacy",
    title: "Privacy Policy | NT Sticks",
    description: "What NT Sticks collects when you order, and how it is used.",
    changefreq: "yearly",
    priority: "0.3",
  },
];

/**
 * Routes that must never be indexed. They carry no sitemap fields because they
 * never belong in one; robots.txt disallows the same list.
 */
export const NOINDEX_ROUTES = [
  { path: "/cart", title: "Your Cart | NT Sticks", description: "Review your NT Sticks order before checkout." },
  { path: "/login", title: "Sign In | NT Sticks", description: "Sign in to place an order and track your NT Sticks deliveries." },
  { path: "/confirmation", title: "Order Confirmed | NT Sticks", description: "Your NT Sticks order has been placed." },
  { path: "/myorders", title: "My Orders | NT Sticks", description: "Your NT Sticks order history." },
  { path: "/dashboard", title: "Order Dashboard | NT Sticks", description: "NT Sticks staff order dashboard." },
  { path: "/trade-rates", title: "Wholesale Rate Card | NT Sticks", description: "Internal wholesale rate card for NT Sticks puja items." },
];

const BY_PATH = Object.fromEntries(
  [...STATIC_ROUTES, ...NOINDEX_ROUTES].map((route) => [route.path, route])
);

/**
 * Meta for one route, shaped for spreading straight into <Seo>.
 * Throws on an unknown path — a typo should fail loudly at build time rather
 * than silently render a page with no title.
 */
export function routeMeta(path) {
  const route = BY_PATH[path];
  if (!route) throw new Error(`No metadata registered for route "${path}"`);
  const meta = { title: route.title, description: route.description };
  if (NOINDEX_ROUTES.some((r) => r.path === path)) meta.noindex = true;
  return meta;
}
